import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import AdminLayout from '../components/admin/AdminLayout';
import Dashboard from '../components/admin/Dashboard';
import PokemonManagement from '../components/admin/PokemonManagement';
import LeagueManagement from '../components/admin/LeagueManagement';
import TeamManagement from '../components/admin/TeamManagement';
import CsvImportManager from '../components/admin/CsvImportManager';
import DataManagementCenter from '../components/admin/DataManagementCenter';
import EventsManagementUnified from '../components/admin/EventsManagementUnified';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('/admin');
  const [searchValue, setSearchValue] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const adminData = localStorage.getItem('adminData');

    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }

    try {
      // Validate token
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;

      if (tokenData.exp < now) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        navigate('/admin/login');
        return;
      }

      setUser(JSON.parse(adminData));
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminData');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (location.pathname !== currentPage) {
      setCurrentPage(location.pathname);
    }
  }, [location.pathname, currentPage]);

  const handlePageChange = (path) => {
    setCurrentPage(path);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    navigate('/admin/login');
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  const getPageInfo = () => {
    switch (currentPage) {
      case '/admin':
      case '/admin/dashboard':
        return {
          title: 'แดชบอร์ด',
          subtitle: 'ภาพรวมระบบจัดการ Pokedex',
          showSearch: false
        };
      case '/admin/pokemon':
      case '/admin/pokemon/list':
        return {
          title: 'จัดการโปเกมอน',
          subtitle: 'จัดการข้อมูลโปเกมอนทั้งหมดในระบบ',
          showSearch: true
        };
      case '/admin/pokemon/import':
        return {
          title: 'นำเข้าข้อมูล CSV',
          subtitle: 'อัพโหลดและนำเข้าข้อมูล Pokemon จากไฟล์ CSV',
          showSearch: false
        };
      case '/admin/data':
      case '/admin/data/center':
        return {
          title: 'Data Management Center',
          subtitle: 'Unified management for Pokemon, imports, and counters',
          showSearch: false
        };
      case '/admin/events':
      case '/admin/categories':
        return {
          title: 'จัดการกิจกรรมและหมวดหมู่',
          subtitle: 'จัดการกิจกรรมและหมวดหมู่กิจกรรม',
          showSearch: false
        };
      case '/admin/leagues':
      case '/admin/leagues/list':
        return {
          title: 'จัดการลีก',
          subtitle: 'จัดการลีกและข้อมูลการแข่งขัน',
          showSearch: true
        };
      case '/admin/teams':
      case '/admin/teams/list':
        return {
          title: 'ทีมแนะนำ',
          subtitle: 'จัดการทีมแนะนำและกลยุทธ์การเล่น',
          showSearch: true
        };
      default:
        return {
          title: 'ระบบจัดการ',
          subtitle: 'จัดการข้อมูล Pokedex',
          showSearch: false
        };
    }
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case '/admin':
      case '/admin/dashboard':
        return <Dashboard />;
      
      case '/admin/pokemon':
      case '/admin/pokemon/list':
        return <PokemonManagement />;
      
      case '/admin/pokemon/import':
        return <CsvImportManager />;
      
      case '/admin/data':
      case '/admin/data/center':
        return <DataManagementCenter />;
      
      case '/admin/events':
      case '/admin/categories':
        return <EventsManagementUnified />;
      
      case '/admin/leagues':
      case '/admin/leagues/list':
        return <LeagueManagement />;
      
      case '/admin/teams':
      case '/admin/teams/list':
        return <TeamManagement />;
      
      default:
        return <Dashboard />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    );
  }

  const pageInfo = getPageInfo();

  return (
    <AdminLayout 
      currentPage={currentPage} 
      onPageChange={handlePageChange}
      onLogout={handleLogout}
      user={user}
      pageTitle={pageInfo.title}
      pageSubtitle={pageInfo.subtitle}
      onSearch={pageInfo.showSearch ? handleSearchChange : null}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
    >
      {renderPageContent()}
    </AdminLayout>
  );
};

export default AdminPage;